import { Badge, BlockStack, Box, Button, ChoiceList, Collapsible, Divider, Icon, InlineStack, Link, SkeletonBodyText, Text } from '@shopify/polaris';
import For from 'components/For';
import { FilterIcon, PlusIcon } from '@shopify/polaris-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dateandtime from 'date-and-time';
import helpers from 'helpers/index';
import { TypedTripData, useGetTripsByJourney } from 'queries/trip.query';
import 'media/css/journey-list.scss';
import TripTimeModal from './tripTimeModal';
import useDidMountEffect from 'components/useDidMountEffect';
import { BillFilledIcon } from '@shopify/polaris-icons';

export default function TripTimeColumn({
  onHourChange,
  onHourTextChange,
  currentJourneyID,
  currentTripSelectedDataCallback,
  currentSelectedDates,
}: {
  onHourChange: (hour: string) => void;
  onHourTextChange: (hour: string) => void;
  currentJourneyID: string | bigint;
  currentTripSelectedDataCallback: (data: TypedTripData | undefined) => void;
  currentSelectedDates: string /** YYYY-MM-DD */;
}) {
  // ---------- STATE ----------
  const [selectedTime, setSelectedTime] = useState<string>(''); // trip id (string)
  const [selectedTimeInText, setSelectedTimeInText] = useState<string>(''); // "HH:mm"

  useEffect(() => {
    setSelectedTimeInText('');
    setSelectedTime('');
  }, [currentSelectedDates]);

  // ⛳️ Thay cho "init 1 lần": lắng nghe URL mỗi khi location.search đổi
  useEffect(() => {
    const q = helpers.ExtractUrl(window.location.search) || {};
    const h = String(q.h ?? '');
    const htext = String(q.htext ?? '');

    // chỉ set khi khác để tránh setState dư thừa + không gây loop
    setSelectedTime((prev) => (prev !== h ? h : prev));
    setSelectedTimeInText((prev) => (prev !== htext ? htext : prev));
  }, []);

  const { data: tripTotalData, isLoading, isSuccess } = useGetTripsByJourney(currentJourneyID, new Date(currentSelectedDates || Date.now()));

  const [tripData, setTripData] = useState<TypedTripData[]>([]);

  useEffect(() => {
    if (tripTotalData) {
      setTripData(tripTotalData);
    }
  }, [tripTotalData, isSuccess]);

  // Cập nhật trip đã chọn (từ id string)
  useEffect(() => {
    if (!tripTotalData || !selectedTime) return;
    const trip = tripTotalData.find((el: TypedTripData) => String(el.id ?? '') === String(selectedTime));
    currentTripSelectedDataCallback(trip);
  }, [tripTotalData, selectedTime, currentTripSelectedDataCallback]);

  // Tránh spam callback nếu giá trị không đổi
  const lastSentRef = useRef<{ h?: string; htext?: string }>({});

  const previousJourneyIdRef = useRef<string | bigint | undefined>();

  useEffect(() => {
    // Đừng reset nếu đang khởi tạo từ URL (lần render đầu tiên)
    if (previousJourneyIdRef.current === undefined) {
      previousJourneyIdRef.current = currentJourneyID;
      return;
    }

    if (previousJourneyIdRef.current !== currentJourneyID) {
      setSelectedTime('');
      setSelectedTimeInText('');
      lastSentRef.current = {};
      currentTripSelectedDataCallback(undefined);
      previousJourneyIdRef.current = currentJourneyID;
    }
  }, [currentJourneyID, currentTripSelectedDataCallback]);

  useDidMountEffect(() => {
    if (selectedTime !== '' && lastSentRef.current.h !== selectedTime) {
      lastSentRef.current.h = selectedTime;
      onHourChange(selectedTime);
    }
  }, [selectedTime]);

  useDidMountEffect(() => {
    if (selectedTimeInText !== '' && lastSentRef.current.htext !== selectedTimeInText) {
      lastSentRef.current.htext = selectedTimeInText;
      onHourTextChange(selectedTimeInText);
    }
  }, [selectedTimeInText]);

  const getDriverRole = (driver_role: string | undefined) => {
    switch (driver_role) {
      case 'driver':
        return (
          <Text as="span" tone="subdued">
            c
          </Text>
        );
      case 'assistant':
        return (
          <Text as="span" tone="subdued">
            p
          </Text>
        );
      default:
        return null;
    }
  };

  /** Modal */
  const [tripTimeShowModal, setTripTimeShowModal] = useState(false);

  // Manifest (object) – CHỈ HIỂN THỊ, KHÔNG CLICK
  const renderTripManifestObject = useCallback((timeSlot: TypedTripData) => {
    const el = timeSlot.trip_manifest;
    if (!el)
      return (
        <Text as="p" variant="bodySm">
          Chưa có xe nào
        </Text>
      );

    const manifestId = String(el.id ?? '');
    const seatingName = el?.seating?.seating_name ?? '---';
    // const totalSeats = el?.seating?.seating_number ?? 0;
    // const occupiedSeats = el?.total_occupied_seats ?? 0;

    return (
      <BlockStack key={`mn_${manifestId}`} gap="200">
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="200">
              {/* <SeatIndicator size={8} totalSeats={totalSeats} occupiedSeats={occupiedSeats} /> */}
              <Text as="p" variant="bodySm" tone="subdued">
                <span>{seatingName}</span>
              </Text>
            </BlockStack>
            {el?.vehicle?.license_plate ? <strong>{String(el?.vehicle?.license_plate).toUpperCase()}</strong> : null}
          </InlineStack>
          <div>
            {el?.trip_manifest_driver?.map((drv, iDrv) => (
              <Text as="p" fontWeight="bold" variant="bodySm" key={`drv_${manifestId}_${iDrv}`}>
                <sup>{getDriverRole(drv.driver_role)}</sup> {drv?.driver_data?.display_name}
              </Text>
            ))}
          </div>
        </BlockStack>
      </BlockStack>
    );
  }, []);

  // Chọn timeslot
  const onSelectTimeslot = useCallback((slot: TypedTripData) => {
    setSelectedTime(String(slot.id ?? ''));
    setSelectedTimeInText(String(slot.time ?? ''));
  }, []);

  // ⛳️ 3) STATE cho lọc tài xế
  const [selectedDriverNames, setSelectedDriverNames] = useState<string[]>([]);

  // ⛳️ 4) Gom tất cả tên tài xế từ dữ liệu (tripData)
  const allDriverNames: string[] = useMemo(() => {
    const names = new Set<string>();
    for (const t of tripData || []) {
      // trip_manifest có thể là object hoặc array => chuẩn hoá
      const manifests = Array<any>(t?.trip_manifest as any);
      for (const mf of manifests) {
        for (const d of mf?.trip_manifest_driver || []) {
          const name = (d?.driver_data?.display_name || '').trim();
          if (name) names.add(name);
        }
      }
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'vi'));
  }, [tripData]);

  const driverChoices = useMemo(() => allDriverNames.map((n) => ({ label: n, value: n })), [allDriverNames]);

  // ⛳️ 5) Check manifest có khớp bộ lọc tài xế không
  const manifestMatchesDriverFilter = useCallback(
    (mf: any) => {
      if (!selectedDriverNames.length) return true;
      const names = (mf?.trip_manifest_driver || []).map((d: any) => (d?.driver_data?.display_name || '').trim());
      return names.some((n: string) => selectedDriverNames.includes(n));
    },
    [selectedDriverNames]
  );

  // Render list trips
  const renderTrips = useCallback(
    (trips: TypedTripData[]) => (
      <div className="trip_time_element trip_time_element_s_down">
        <For each={trips} fallback={<Text as="p">Không có chuyến nào</Text>}>
          {(timeSlot: TypedTripData, index) => {
            const isActive = selectedTime && selectedTime === (timeSlot.id ?? '');
            // SAFE percentage
            const totalSeat = timeSlot?.trip_manifest?.seating?.seating_available_count ?? 0;
            const totalBookedSeat = timeSlot?.current_ticket_count ?? 0;
            const rawPct = totalSeat > 0 ? (totalBookedSeat / totalSeat) * 100 : 0;
            const percentage = Math.max(0, Math.min(100, Math.floor(rawPct)));
            const is_exported_invoice = timeSlot?.trip_manifest?.is_exported_invoice === 1;

            // NEW: Tính “dim” theo bộ lọc tài xế
            const manifests = Array<any>(timeSlot?.trip_manifest as any);
            const hasAnyMatch = manifests.some((mf) => manifestMatchesDriverFilter(mf));
            const shouldDim = selectedDriverNames.length > 0 && !hasAnyMatch;

            return (
              <a
                href="#void"
                key={`timeslots___${String(timeSlot.id ?? index)}`}
                onClick={(e) => {
                  e.preventDefault();
                  onSelectTimeslot(timeSlot);
                }}
                className={`link-card timeslot_element ${isActive ? 'active' : ''} ${shouldDim ? 'is-dimmed' : ''}`}
                role="button"
                tabIndex={0}
                style={{ position: 'relative' }}
                aria-pressed={!!isActive}
                title="Chọn giờ này"
              >
                {/* progress bar không ăn click */}
                <div className="timeslot_progress_bar" aria-hidden style={{ pointerEvents: 'none' }}>
                  <div
                    className={`timeslot_progress_bar__inner ${percentage >= 90 ? '' : 'notice'}`}
                    style={{ width: `${percentage}%`, pointerEvents: 'none' }}
                  />
                </div>

                <Box zIndex="10" position="relative">
                  <BlockStack gap="200">
                    <InlineStack gap="200" align="space-between" blockAlign="center">
                      <Text as="span" tone="critical" fontWeight="bold">
                        <InlineStack>
                          <span>{is_exported_invoice ? <Icon source={BillFilledIcon} /> : null}</span>
                          <span>{timeSlot.time ?? '--:--'} </span>
                        </InlineStack>
                      </Text>
                      <Text as="span" tone="subdued" variant="bodyXs">{`${totalBookedSeat}/${totalSeat}`}</Text>
                    </InlineStack>

                    <BlockStack gap="200">{renderTripManifestObject(timeSlot)}</BlockStack>
                  </BlockStack>
                </Box>
              </a>
            );
          }}
        </For>
      </div>
    ),
    [selectedTimeInText, selectedTime, selectedDriverNames.length, renderTripManifestObject, manifestMatchesDriverFilter, onSelectTimeslot]
  );

  /** Mở bộ lọc */
  const [openDriverFilter, setOpenDriverFilter] = useState(false);
  const handleToggleDriverFilter = useCallback(() => setOpenDriverFilter((open) => !open), []);

  return (
    <>
      {/* ⛳️ 7) Khối UI bộ lọc tài xế (đặt trên danh sách trip) */}
      <InlineStack align="space-between" blockAlign="center">
        <Text as="span" fontWeight="bold">
          Ngày {dateandtime.format(new Date(currentSelectedDates), 'DD-MM-YYYY', false)}
        </Text>
        <InlineStack gap="400" align="space-between" blockAlign="end">
          <Link onClick={handleToggleDriverFilter}>Lọc tài xế</Link>
          <Icon source={FilterIcon} />
        </InlineStack>
      </InlineStack>
      <br />
      <Collapsible open={openDriverFilter} id="basic-collapsible" transition={{ duration: '100ms', timingFunction: 'ease-in-out' }} expandOnPrint>
        <Box padding="300" background="bg-surface-secondary" borderRadius="200">
          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                Lọc theo tài xế
              </Text>
              <Button
                onClick={() => setSelectedDriverNames([])}
                size="micro"
                disabled={!selectedDriverNames.length}
                accessibilityLabel="Clear driver filter"
              >
                Xóa lọc
              </Button>
            </InlineStack>

            {driverChoices.length ? (
              <ChoiceList title="" allowMultiple selected={selectedDriverNames} onChange={(v) => setSelectedDriverNames(v)} choices={driverChoices} />
            ) : (
              <Text as="p" tone="subdued" variant="bodySm">
                Không có tài xế nào trong dữ liệu hiện tại.
              </Text>
            )}
          </BlockStack>
        </Box>
      </Collapsible>
      <BlockStack gap="400">{isLoading ? <SkeletonBodyText /> : renderTrips(tripData || [])}</BlockStack>

      <br />
      <div key={`last_time_element`} className="clickable">
        <Button icon={PlusIcon} variant="plain" onClick={() => setTripTimeShowModal(true)}>
          Thêm giờ chạy
        </Button>
      </div>

      <TripTimeModal
        onClose={(output) => {
          if (output) {
            setSelectedTime(String(output.id ?? ''));
            setSelectedTimeInText(String(output.time ?? ''));
          }
          setTripTimeShowModal(false);
        }}
        show={tripTimeShowModal}
        current_journey_id={currentJourneyID}
        current_departure_date={new Date(currentSelectedDates || Date.now())}
      />
    </>
  );
}

